'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { BadgeCheck, ChevronRight, Flag, Lock, Phone, ShieldCheck, Star, Store, Wallet } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import { formatDateNormal } from '@/utils/util'
import ReportVendorDialog from '@/components/blocks/ReportVendorDialog'
import Image from 'next/image'
import verifyImg from '@/assets/user-verification.png'

export default function Status({ vendor }: { vendor: any }) {
  const verified = !!vendor?.isDanVerified
  return verified ? <Verified vendor={vendor} /> : <Unverified vendor={vendor} />
}

function Verified({ vendor }: { vendor: any }) {
  const pathname = usePathname() || ''
  const [reportOpen, setReportOpen] = useState(false)
  const openedDate = vendor?.createdAt ? formatDateNormal(new Date(vendor.createdAt)) : null

  return (
    <section className="bg-cream pb-20 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <Crumbs vendorName={vendor?.name} verified pathname={pathname} />
      </div>

      <div className="relative w-full bg-[#E8E2FF] overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/40 pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-40 h-40 rounded-full bg-foreground/5 pointer-events-none" />

        <div className="relative mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-16 sm:py-20 flex flex-col items-center text-center">
          <Seal />
          <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <BadgeCheck className="size-4" /> Баталгаажсан дэлгүүр
          </div>
          <h1 className="mt-3 font-bold font-gilroy tracking-[-0.02em] leading-tight text-foreground max-w-2xl" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
            <span>{vendor?.name}</span> нь баталгаажсан худалдаа эрхлэгч.
          </h1>
          <p className="mt-5 text-[15px] sm:text-[16px] leading-[1.65] text-foreground/65 max-w-xl">
            Энэхүү дэлгүүрийн эзэмшигчийн хувийн мэдээллийг Улсын бүртгэлийн мэдээллийн санд тулгуурлан баталгаажуулсан.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-230 px-4 sm:px-6 lg:px-10 -mt-10 sm:-mt-14 relative z-10">
        <div className="bg-white rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
            <div className="px-7 py-8 md:py-12 bg-foreground text-cream flex md:flex-col items-center justify-center gap-4 md:gap-5">
              <div className="h-16 w-16 rounded-full bg-cream/15 inline-flex items-center justify-center border border-cream/25">
                <BadgeCheck className="size-9" strokeWidth={2.25} />
              </div>
              <div className="text-center">
                <div className="text-[13px] font-semibold">Сертификат</div>
                <div className="mt-0.5 text-[12px] opacity-70">Shop.mn</div>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-baseline justify-between mb-6">
                <div className="text-[13px] font-semibold text-foreground">Бүртгэлийн мэдээлэл</div>
                <div className="text-[12px] text-emerald-700 inline-flex items-center gap-1.5">
                  <span className="inline-block size-1.5 rounded-full bg-emerald-500" /> Идэвхтэй
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <Field label="Дэлгүүрийн нэр" value={vendor?.name || '—'} />
                {openedDate && <Field label="Нээгдсэн огноо" value={openedDate} />}
              </div>

              <div className="mt-8 pt-6 border-t border-foreground/10 flex flex-wrap items-center gap-3 justify-between">
                <div className="inline-flex items-center gap-2 text-[12px] text-foreground/55">
                  <Lock className="size-3.5" /> Хувийн мэдээлэл хамгаалагдсан
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  <a href={navigateUrl('/', pathname)} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors">
                    <Store className="size-4" /> Дэлгүүр үзэх
                  </a>
                  <button onClick={() => setReportOpen(true)} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border-1.5 border-foreground/15 bg-transparent text-foreground text-[13px] font-semibold hover:border-foreground/30 transition-colors cursor-pointer">
                    <Flag className="size-4" /> Дэлгүүрийг мэдэгдэх
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center text-[12px] text-foreground/45">
          Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой ба худалдан авалтын аливаа баталгаа болохгүй.
        </div>
      </div>

      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </section>
  )
}

function Unverified({ vendor }: { vendor: any }) {
  const pathname = usePathname() || ''
  const [reportOpen, setReportOpen] = useState(false)

  const checks = [
    { icon: Wallet, t: 'Төлбөрөө хамгаалалттай аргаар хий', s: 'Боломжтой бол QPay, StorePay зэрэг буцаалт хийх боломжтой аргыг сонго.' },
    { icon: Star, t: 'Бусдын үнэлгээг уншаарай', s: 'Бүтээгдэхүүний хуудсан дахь бодит сэтгэгдэл, үнэлгээг нягтал.' },
    { icon: Phone, t: 'Холбоо барих мэдээллийг шалга', s: 'Утас, имэйл, хаяг бодитоор ажиллаж байгаа эсэхийг нягтал.' },
    { icon: Flag, t: 'Сэжигтэй бол мэдэгд', s: 'Хуурамч дэлгүүр гэж бодож байвал бидэнд шууд мэдэгдээрэй.' },
  ]

  return (
    <section className="bg-cream pb-20 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <Crumbs vendorName={vendor?.name} verified={false} pathname={pathname} />

        <div className="mx-auto max-w-205">
          <div className="mt-2 flex items-center gap-5 sm:gap-6">
            <Image src={verifyImg} alt="" aria-hidden className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 select-none" />
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C2453F]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E8635F]" /> Баталгаажаагүй
              </div>
              <h1 className="mt-1.5 font-gilroy font-bold tracking-[-0.02em] leading-[1.05] text-foreground" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}>
                {vendor?.name}
              </h1>
            </div>
          </div>

          <p className="mt-5 text-[15px] sm:text-[16px] leading-[1.65] text-foreground/65 max-w-xl">
            Энэ дэлгүүрийн эзэмшигчийн хувийн мэдээлэл Улсын бүртгэлээр баталгаажаагүй байна. Худалдан авалт хийхээсээ өмнө доорх зүйлсийг нягтлаарай.
          </p>

          <div className="mt-10 border-t border-foreground/10 pt-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50">Худалдан авахаасаа өмнө</div>
            <ul className="mt-5 space-y-5">
              {checks.map((c, i) => {
                const Icon = c.icon
                return (
                  <li key={i} className="flex gap-4">
                    <div className="mt-0.5 size-9 shrink-0 grid place-items-center rounded-full bg-[#FCECEC] text-[#C2453F]">
                      <Icon className="size-4" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-foreground">{c.t}</div>
                      <div className="mt-0.5 text-[13.5px] leading-relaxed text-foreground/55">{c.s}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch gap-3">
            <a href={navigateUrl('/', pathname)} className="inline-flex items-center justify-center gap-2 h-13 px-6 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors">
              <Store className="size-4" /> Дэлгүүр үзэх
            </a>
            <button onClick={() => setReportOpen(true)} className="inline-flex items-center justify-center gap-2 h-13 px-6 rounded-full border-1.5 border-foreground/15 bg-transparent text-foreground text-[14px] font-semibold hover:border-foreground/30 transition-colors cursor-pointer">
              <Flag className="size-4" /> Дэлгүүрийг мэдэгдэх
            </button>
          </div>

          <p className="mt-10 text-[12px] text-foreground/45 border-t border-foreground/10 pt-5">
            Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой ба худалдан авалтын аливаа баталгаа болохгүй.
          </p>
        </div>
      </div>

      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </section>
  )
}

function Crumbs({ vendorName, verified, pathname }: { vendorName?: string, verified: boolean, pathname: string }) {
  return (
    <nav className="hidden md:flex items-center gap-1.5 text-[13px] text-foreground/55 mb-6">
      <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
      <ChevronRight className="size-3 opacity-50" />
      <a href={navigateUrl('/', pathname)} className="hover:text-foreground">{vendorName || 'Дэлгүүр'}</a>
      <ChevronRight className="size-3 opacity-50" />
      <span className="text-foreground font-medium">{verified ? 'Баталгаажсан төлөв' : 'Баталгаажаагүй төлөв'}</span>
    </nav>
  )
}

function Seal() {
  return (
    <div className="size-20 rounded-full bg-foreground text-cream inline-flex items-center justify-center">
      <ShieldCheck className="size-10" strokeWidth={2} />
    </div>
  )
}

function Field({ label, value }: { label: string, value: string }) {
  return (
    <div>
      <div className="text-[13px] text-foreground/55">{label}</div>
      <div className="mt-1 text-[15px] font-semibold text-foreground">{value}</div>
    </div>
  )
}
